import { api } from "@/api/client";
import type {
  ChangePasswordPayload,
  ConfirmTwoFAPayload,
  DeleteTwoFAPayload,
  LoginPayload,
  LoginResponseType,
  SetTwoFAResponse,
  TwoFALoginPayload,
  TwoFATokenLoginPayload,
} from "@/api/mutations/auth/types";
import { queryKeys } from "@/api/queryKeys";
import { useMutation, useQueryClient } from "@tanstack/react-query";

export const useChangePassword = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: ChangePasswordPayload) =>
      api.post("/auth/change-password", payload).then((res) => res.data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.me] });
    },
  });
};

export const useLogin = () => {
  return useMutation({
    mutationFn: async (payload: LoginPayload) =>
      api.post<LoginResponseType>("/auth/login", payload).then((res) => res.data),
  });
};

export const useLogout = () => {
  return useMutation({
    mutationFn: async () => {
      return api.post("/auth/logout").then((res) => res.data);
    },
  });
};

export const useAcceptOAuthCredentials = () => {
  return useMutation({
    mutationFn: async (payload: { refreshToken: string }) => {
      return api.post("/auth/accept-oauth-credentials", payload).then((res) => res.data);
    },
  });
};

export const useUnLinkGoogleAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => api.delete("/auth/unlink-google").then((res) => res.data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.me] });
    },
  });
};

export const useSetTwoFA = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => api.put<SetTwoFAResponse>("/auth/set-two-fa").then((res) => res.data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.me] });
    },
  });
};

export const useConfirmTwoFA = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: ConfirmTwoFAPayload) =>
      api.post("/auth/confirm-two-fa", payload).then((res) => res.data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.me] });
    },
  });
};

export const useDeleteTwoFA = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: DeleteTwoFAPayload) =>
      api.put("/auth/delete-two-fa", payload).then((res) => res.data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.me] });
    },
  });
};

export const useTwoFALogin = () => {
  return useMutation({
    mutationFn: async (payload: TwoFALoginPayload) =>
      api.post<LoginResponseType>("/auth/login-with-two-fa", payload).then((res) => res.data),
  });
};

export const useTwoFATokenLogin = () => {
  return useMutation({
    mutationFn: async (payload: TwoFATokenLoginPayload) =>
      api.post<LoginResponseType>("/auth/two-fa-token-login", payload).then((res) => res.data),
  });
};
